/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable no-return-await */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Api from 'util/Api';
import api from 'constants/api';
import { useSelector } from 'react-redux';
import { authProfile } from 'redux/auth/selectors';
import { notification } from 'antd';
import dayjs from 'dayjs';
import { SegmentedOptions } from 'antd/es/segmented';

export const DAYS_TO_EXPIRE = 30;

interface Option {
  value: number;
  name: string;
}

// TODO Estos enums no conectan bien. Consultar

const PackageSizeOptions: Option[] = [
  { value: 0, name: 'undefined' },
  { value: 1, name: 'small' },
  { value: 2, name: 'medium' },
  { value: 3, name: 'large' },
];

const DeliveryTermOptions: Option[] = [
  { value: 0, name: 'undefined' },
  { value: 1, name: 'standard' },
  { value: 2, name: 'priority' },
  { value: 3, name: 'express' },
];

export enum ExpiredFilter {
  ALL = 'ALL',
  EXPIRED = 'EXPIRED',
  ACTIVE = 'ACTIVE',
  CLOSE_TO_EXPIRATION = 'CLOSE_TO_EXPIRATION',
}

export const expirationFilterOptions: SegmentedOptions<string> = [
  { label: 'Todas', value: ExpiredFilter.ALL },
  { label: 'Activas', value: ExpiredFilter.ACTIVE },
  { label: 'Próximas a vencer', value: ExpiredFilter.CLOSE_TO_EXPIRATION },
  { label: 'Vencidas', value: ExpiredFilter.EXPIRED },
];

interface GetRatesQueries {
  ownerId?: number | null;
  marketplace_id?: number | null;
  package_size?: number | string | null;
  delivery_term?: number | string | null;
  page?: number | null;
  size?: number | null;
  expiration_status?: ExpiredFilter;
  days_to_expire?: number | null;
}

interface GetLastMileQueries extends GetRatesQueries {
  zone_id?: number | null;
}

interface GetTrunkQueries extends GetRatesQueries {
  cdc_origin_id?: number | null;
  cdc_destination_id?: number | null;
}

interface GetRatesResponse {
  data: {
    total_elements: number;
    page_size: number;
    page_number: number;
    items?: Rate[];
  };
  message?: string;
}

export interface Rate {
  id?: number;
  rate?: number;
  currency?: string;
  cost?: number;
  marketplace_name?: string;
  marketplace_id?: number;
  package_size?: string | number;
  delivery_term?: string | number;
  valid_since?: Date;
  valid_until?: Date;
  updated?: boolean;
}

export interface LastMileRate extends Rate {
  zone_name?: string;
  zone_id?: number;
}

export interface TrunkRate extends Rate {
  cdc_origin_id?: string;
  cdc_destination_id?: string;
}

interface CreateLastMileRequest extends LastMileRate {
  logistics_operator_id: number;
  tenant: number;
  country_id: number;
}

interface CreateTrunkRequest extends TrunkRate {
  logistics_operator_id: number;
  tenant: number;
  country_id: number;
}

const transformRateFromApi = (rate: Rate) => ({
  ...rate,
  package_size:
    PackageSizeOptions.find((size: Option) => size.name === rate.package_size)
      ?.value ?? undefined,
  delivery_term:
    DeliveryTermOptions.find((term: Option) => term.name === rate.delivery_term)
      ?.value ?? undefined,
});

const transformRateToApi = (rate: Rate) => ({
  ...rate,
  package_size:
    PackageSizeOptions.find((size: Option) => size.value === rate.package_size)
      ?.name ?? PackageSizeOptions[0].name,
  delivery_term:
    DeliveryTermOptions.find(
      (term: Option) => term.value === rate.delivery_term,
    )?.name ?? DeliveryTermOptions[0].name,
});

const transformQueryToApi = (query: GetLastMileQueries | GetTrunkQueries) => ({
  ...query,
  package_size:
    PackageSizeOptions.find((size: Option) => size.value === query.package_size)
      ?.name ?? (query.package_size === 'undefined' ? 'undefined' : undefined),
  delivery_term:
    DeliveryTermOptions.find(
      (term: Option) => term.value === query.delivery_term,
    )?.name ?? (query.delivery_term === 'undefined' ? 'undefined' : undefined),
  page: query.page ?? 0,
  size: query.size ?? 10,
});

const getLastMileRates = async (
  logisticOperatorId: string,
  queries: GetLastMileQueries,
): Promise<GetRatesResponse> => {
  const response = await Api.apiAxios.get<GetRatesResponse>(
    api.rates.lastMile.allByOpl(logisticOperatorId),
    {
      params: transformQueryToApi(queries),
    },
  );

  if (!Api.isSuccessResponse(response)) {
    throw new Error(response.data.message);
  }

  return {
    data: {
      ...response.data.data,
      items: response.data.data.items?.map(transformRateFromApi),
    },
    message: response.data.message,
  };
};

const getTrunkRates = async (
  logisticOperatorId: string,
  queries: GetTrunkQueries,
): Promise<GetRatesResponse> => {
  const response = await Api.apiAxios.get<GetRatesResponse>(
    api.rates.trunk.allByOpl(logisticOperatorId),
    {
      params: transformQueryToApi(queries),
    },
  );

  if (!Api.isSuccessResponse(response)) {
    throw new Error(response.data.message);
  }

  return {
    data: {
      ...response.data.data,
      items: response.data.data.items?.map(transformRateFromApi),
    },
    message: response.data.message,
  };
};

const getLastMileRate = async (rateId: number) => {
  const response = await Api.apiAxios.get<LastMileRate>(
    `${api.rates.lastMile.byId(rateId)}`,
  );

  if (!Api.isSuccessResponse(response)) {
    throw new Error('No se ha podido obtener la tarifa');
  }

  return transformRateFromApi(response.data);
};

const getTrunkRate = async (rateId: number) => {
  const response = await Api.apiAxios.get<TrunkRate>(
    `${api.rates.trunk.byId(rateId)}`,
  );

  if (!Api.isSuccessResponse(response)) {
    throw new Error('No se ha podido obtener la tarifa');
  }

  return transformRateFromApi(response.data);
};

const createLastMileRate = async (body: CreateLastMileRequest) => {
  const response = await Api.apiAxios.post<{ message: string }>(
    api.rates.lastMile.root,
    transformRateToApi(body),
    { headers: { tenant: body.tenant } },
  );

  if (!Api.isSuccessResponse(response)) {
    throw new Error('No se ha podido crear la tarifa');
  }

  return true;
};

const createTrunkRate = async (body: CreateTrunkRequest) => {
  const response = await Api.apiAxios.post<{ message: string }>(
    api.rates.trunk.root,
    transformRateToApi(body),
    { headers: { tenant: body.tenant } },
  );

  if (!Api.isSuccessResponse(response)) {
    throw new Error('No se ha podido crear la tarifa');
  }

  return true;
};

const modifyLastMileRate = async (body: CreateLastMileRequest) => {
  const response = await Api.apiAxios.put<{ message: string }>(
    api.rates.lastMile.root,
    transformRateToApi(body),
    { headers: { tenant: body.tenant } },
  );

  if (!Api.isSuccessResponse(response)) {
    throw new Error('No se ha podido modificar la tarifa');
  }

  return true;
};

const modifyTrunkRate = async (body: CreateTrunkRequest) => {
  const response = await Api.apiAxios.put<{ message: string }>(
    api.rates.trunk.root,
    transformRateToApi(body),
    { headers: { tenant: body.tenant } },
  );

  if (!Api.isSuccessResponse(response)) {
    throw new Error('No se ha podido modificar la tarifa');
  }

  return true;
};

// HOOKS

export const useRate = (
  type: 'lastMile' | 'trunk',
  rateId?: number,
  enabled = false,
) => {
  return useQuery<LastMileRate | TrunkRate, Error>({
    queryKey: ['lastMileRate', rateId],
    queryFn: () => {
      if (type === 'lastMile') return getLastMileRate(rateId!);
      return getTrunkRate(rateId!);
    },
    retry: 1,
    enabled: !!rateId && enabled,
  });
};

export const useCreateLastMileRate = () => {
  const queryClient = useQueryClient();
  const profile = useSelector(authProfile);

  return useMutation({
    mutationFn: async (rate: LastMileRate) =>
      await createLastMileRate({
        ...rate,
        valid_since: dayjs(rate.valid_since).startOf('day').toDate(),
        valid_until: rate.valid_until
          ? dayjs(rate.valid_until).endOf('day').toDate()
          : undefined,
        logistics_operator_id: profile?.logisticOperatorID,
        tenant: profile?.tenantId,
        country_id: profile?.countryID,
      }),
    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: ['lastMile'],
      });
      const previousRatesData = queryClient.getQueryData(['lastMile']);
      return {
        previousRatesData,
      };
    },
    onError: (e) => {
      if (e instanceof Error)
        notification.error({
          message: 'Ocurrió un error al crear la tarifa',
          description: e.message,
        });
    },
    onSuccess: () => {
      notification.success({
        message: 'Creación de tarifa',
        description:
          'Se confirmó la creación de la tarifa. Los cambios pueden tardar unos segundos en visualizarse.',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ['lastMile'],
      });
    },
  });
};

export const useCreateTrunkRate = () => {
  const queryClient = useQueryClient();
  const profile = useSelector(authProfile);

  return useMutation({
    mutationFn: async (rate: TrunkRate) =>
      await createTrunkRate({
        ...rate,
        valid_since: dayjs(rate.valid_since).startOf('day').toDate(),
        valid_until: rate.valid_until
          ? dayjs(rate.valid_until).endOf('day').toDate()
          : undefined,
        logistics_operator_id: profile?.logisticOperatorID,
        tenant: profile?.tenantId,
        country_id: profile?.countryID,
      }),
    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: ['trunk'],
      });
    },
    onError: (e) => {
      if (e instanceof Error)
        notification.error({
          message: 'Ocurrió un error al crear la tarifa',
          description: e.message,
        });
    },
    onSuccess: () => {
      notification.success({
        message: 'Creación de tarifa',
        description:
          'Se confirmó la creación de la tarifa. Los cambios pueden tardar unos segundos en visualizarse.',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ['trunk'],
      });
    },
  });
};

export const useModifyLastMileRate = () => {
  const queryClient = useQueryClient();
  const profile = useSelector(authProfile);

  return useMutation({
    mutationFn: async (rate: LastMileRate) =>
      await modifyLastMileRate({
        ...rate,
        valid_since: dayjs(rate.valid_since).startOf('day').toDate(),
        valid_until: rate.valid_until
          ? dayjs(rate.valid_until).endOf('day').toDate()
          : undefined,
        logistics_operator_id: profile?.logisticOperatorID,
        tenant: profile?.tenantId,
        country_id: profile?.countryID,
      }),
    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: ['lastMile'],
      });
    },
    onError: (e) => {
      if (e instanceof Error) {
        notification.error({
          message: 'Ocurrió un error al modificar la tarifa',
          description: e.message,
        });
      }
    },
    onSuccess: () => {
      notification.success({
        message: 'Edición de tarifa',
        description:
          'Se confirmó la edición de la tarifa. Los cambios pueden tardar unos segundos en visualizarse.',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ['lastMile'],
      });
    },
  });
};

export const useModifyTrunkRate = () => {
  const queryClient = useQueryClient();
  const profile = useSelector(authProfile);

  return useMutation({
    mutationFn: async (rate: TrunkRate) =>
      await modifyTrunkRate({
        ...rate,
        valid_since: dayjs(rate.valid_since).startOf('day').toDate(),
        valid_until: rate.valid_until
          ? dayjs(rate.valid_until).endOf('day').toDate()
          : undefined,
        logistics_operator_id: profile?.logisticOperatorID,
        tenant: profile?.tenantId,
        country_id: profile?.countryID,
      }),
    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: ['trunk'],
      });
    },
    onError: (e) => {
      if (e instanceof Error)
        notification.error({
          message: 'Ocurrió un error al modificar la tarifa',
          description: e.message,
        });
    },
    onSuccess: () => {
      notification.success({
        message: 'Edición de tarifa',
        description:
          'Se confirmó la edición de la tarifa. Los cambios pueden tardar unos segundos en visualizarse.',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ['trunk'],
      });
    },
  });
};

export const useLastMileRates = (queries: GetLastMileQueries) => {
  const profile = useSelector(authProfile);

  const daysToExpire =
    queries?.expiration_status === ExpiredFilter.CLOSE_TO_EXPIRATION ||
    queries?.expiration_status === ExpiredFilter.ALL
      ? DAYS_TO_EXPIRE
      : undefined;

  return useQuery<GetRatesResponse, Error>({
    queryKey: [
      'lastMile',
      profile.logisticOperatorID,
      queries?.marketplace_id,
      queries?.package_size,
      queries?.delivery_term,
      queries?.expiration_status,
      daysToExpire,
      queries?.page,
      queries?.size,
    ],
    queryFn: () => getLastMileRates(profile.logisticOperatorID, queries),
    retry: 1,
    refetchOnMount: false,
    staleTime: 300000,
  });
};

export const useTrunkRates = (queries: GetTrunkQueries) => {
  const profile = useSelector(authProfile);

  const daysToExpire =
    queries?.expiration_status === ExpiredFilter.CLOSE_TO_EXPIRATION ||
    queries?.expiration_status === ExpiredFilter.ALL
      ? DAYS_TO_EXPIRE
      : undefined;

  return useQuery<GetRatesResponse, Error>({
    queryKey: [
      'trunk',
      profile.logisticOperatorID,
      queries?.marketplace_id,
      queries?.cdc_origin_id,
      queries?.cdc_destination_id,
      queries?.package_size,
      queries?.delivery_term,
      queries?.expiration_status,
      daysToExpire,
      queries?.page,
      queries?.size,
    ],
    queryFn: () => getTrunkRates(profile.logisticOperatorID, queries),
    retry: 1,
    refetchOnMount: false,
    staleTime: 300000,
  });
};

export const useDeleteLastMile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (rate: LastMileRate) =>
      await Api.apiAxios.delete(`${api.rates.lastMile.byId(rate.id)}`),
    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: ['lastMile'],
      });
    },
    onError: (e) => {
      if (e instanceof Error)
        notification.error({
          message: 'Ocurrió un error al eliminar la tarifa',
          description: e.message,
        });
    },
    onSuccess: () => {
      notification.success({
        message: 'Eliminación de tarifa',
        description:
          'Se confirmó la eliminación de la tarifa. Los cambios pueden tardar unos segundos en visualizarse.',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ['lastMile'],
      });
    },
  });
};

export const useDeleteTrunk = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (rate: TrunkRate) =>
      await Api.apiAxios.delete(`${api.rates.trunk.byId(rate.id)}`),
    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: ['trunk'],
      });
    },
    onError: (e) => {
      if (e instanceof Error)
        notification.error({
          message: 'Ocurrió un error al eliminar la tarifa',
          description: e.message,
        });
    },
    onSuccess: () => {
      notification.success({
        message: 'Eliminación de tarifa',
        description:
          'Se confirmó la eliminación de la tarifa. Los cambios pueden tardar unos segundos en visualizarse.',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ['trunk'],
      });
    },
  });
};
